import clsx from 'clsx'
import ButtonBack from 'components/FormControls/back-button'
import renderSelectorField from 'components/FormControls/selector'
import { Alert, Button, Container, Icon, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import styles from 'modules/Forms/Account/Create/account-form-creation.module.scss'

const validationSchema = Yup.object().shape({
  profile: Yup.string(),
  resource: Yup.string(),
  id: Yup.string().required('common::validation_messages.required'),
})

let StatisticsAccountConnectForm = ({
  handleSubmit,
  submitting,
  autofill,
  hierarchy,
  onGoBack = null,
  onSubmit = () => {},
  form,
}) => {
  const { t } = useTranslation()
  const selector = formValueSelector(form)

  const { profile, resource, cantGetHierarchy } = useSelector((state) => ({
    profile: selector(state, 'profile'),
    resource: selector(state, 'resource'),
    cantGetHierarchy: state['statistics/accountConnect'].cantGetHierarchy,
  }))

  const [formOptions, setFormOptions] = useState({
    profiles: [],
    resources: [],
    ids: [],
  })

  useEffect(() => {
    const opts = {}
    opts.profiles =
      hierarchy.map((profile) => ({
        label: profile.name,
        value: profile.id + '',
      })) || []

    opts.resources =
      hierarchy
        .find((prof) => prof.id + '' === profile)
        ?.web_properties.map((res) => ({
          label: res.name,
          value: res.id + '',
        })) || []

    opts.ids =
      hierarchy
        .find((prof) => prof.id + '' === profile)
        ?.web_properties.find((webp) => webp.id + '' === resource)
        ?.profiles.map((profile) => ({
          label: profile.name,
          value: profile.id + '',
        })) || []

    setFormOptions(opts)
    autofill('id', '')
  }, [hierarchy, profile, resource])

  useEffect(() => {
    autofill('resource', '')
  }, [profile])

  const submit = useCallback((values) => onSubmit(values), [])

  return (
    <div className={formStyles['formContainer']}>
      <form onSubmit={handleSubmit(submit)}>
        <div className={'margin-bottom-16'}>
          <Field
            startIcon={
              <Icon name={'user'} size={'l'} color={'Label-GU-Primary'} />
            }
            size={'l'}
            label={t('statistics::forms.account_connect.labels.profile')}
            name={'profile'}
            items={formOptions.profiles}
            fullWidth
            component={renderSelectorField}
            disabled={formOptions.profiles.length === 0}
            onChange={(value) => {
              autofill('profile', value)
            }}
            t={t}
          />
        </div>
        <div className={'margin-bottom-16'}>
          <Field
            startIcon={
              <Icon name={'category'} size={'l'} color={'Label-GU-Primary'} />
            }
            size={'l'}
            label={t('statistics::forms.account_connect.labels.resource')}
            name={'resource'}
            disabled={formOptions.resources.length === 0}
            items={formOptions.resources}
            fullWidth
            component={renderSelectorField}
            onChange={(value) => autofill('resource', value)}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            disabled={formOptions.ids.length === 0}
            startIcon={
              <Icon name={'category'} size={'l'} color={'Label-GU-Primary'} />
            }
            size={'l'}
            label={t('statistics::forms.account_connect.labels.id')}
            name={'id'}
            items={formOptions.ids}
            fullWidth
            component={renderSelectorField}
            t={t}
            onChange={(value) => autofill('id', value)}
          />
        </div>

        {cantGetHierarchy && (
          <div className={'margin-bottom-16'}>
            <Container
              display={'flex'}
              flexDirection={'column'}
              flexJustify={'center'}
              flexAlign={'center'}
            >
              <Alert severity={'error'}>
                <Typography variant={'p8'} noMargin>
                  {t('statistics::forms.account_connect.trouble_description')}
                </Typography>
              </Alert>
            </Container>
          </div>
        )}

        <div
          className={clsx(
            formStyles['formControl__action-buttons'],
            styles['action-buttons'],
          )}
        >
          <ButtonBack
            onGoBack={onGoBack}
            disalbed={submitting}
            label={
              cantGetHierarchy && t('statistics::forms.account_connect.back')
            }
          />

          <Button
            disabled={submitting || hierarchy.length === 0}
            size="l"
            variant="primary"
            type="submit"
          >
            {t('common::form_buttons.submit')}
          </Button>
        </div>
      </form>
    </div>
  )
}

StatisticsAccountConnectForm = reduxForm({
  form: 'StatisticsAccountConnectForm',
  initialValues: {
    profile: '',
    resource: '',
    id: null,
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(StatisticsAccountConnectForm)

StatisticsAccountConnectForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  autofill: PropTypes.func,
  hierarchy: PropTypes.array,
  onGoBack: PropTypes.func,
  onSubmit: PropTypes.func,
  form: PropTypes.string,
}

export default StatisticsAccountConnectForm
